import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CohortDiscussion {
  id: string;
  cohort_id: string;
  user_id: string;
  title: string;
  content: string;
  image_url: string | null;
  link_url: string | null;
  is_pinned: boolean | null;
  is_archived: boolean | null;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
  reply_count?: number;
}

export interface CohortDiscussionReply {
  id: string;
  discussion_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export function useCohortDiscussions(cohortId: string) {
  return useQuery({
    queryKey: ["cohort-discussions", cohortId],
    queryFn: async () => {
      const { data: discussions, error } = await supabase
        .from("cohort_discussions")
        .select("*")
        .eq("cohort_id", cohortId)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      const userIds = [...new Set(discussions.map((d) => d.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const discussionIds = discussions.map((d) => d.id);
      const { data: replyCounts } = await supabase
        .from("cohort_discussion_replies")
        .select("discussion_id")
        .in("discussion_id", discussionIds);

      const replyCountMap: Record<string, number> = {};
      replyCounts?.forEach((r) => {
        replyCountMap[r.discussion_id] = (replyCountMap[r.discussion_id] || 0) + 1;
      });

      return discussions.map((discussion) => ({
        ...discussion,
        author: profiles?.find((p) => p.id === discussion.user_id),
        reply_count: replyCountMap[discussion.id] || 0,
      })) as CohortDiscussion[];
    },
    enabled: !!cohortId,
  });
}

export function useCreateCohortDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cohortId,
      title,
      content,
      userId,
      image_url,
      link_url,
    }: {
      cohortId: string;
      title: string;
      content: string;
      userId: string;
      image_url?: string;
      link_url?: string;
    }) => {
      const { data, error } = await supabase
        .from("cohort_discussions")
        .insert({
          cohort_id: cohortId,
          title,
          content,
          user_id: userId,
          image_url: image_url || null,
          link_url: link_url || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["cohort-discussions", variables.cohortId] });
    },
  });
}

export function useCohortDiscussionReplies(discussionId: string) {
  return useQuery({
    queryKey: ["cohort-discussion-replies", discussionId],
    queryFn: async () => {
      const { data: replies, error } = await supabase
        .from("cohort_discussion_replies")
        .select("*")
        .eq("discussion_id", discussionId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const userIds = [...new Set(replies.map((r) => r.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      return replies.map((reply) => ({
        ...reply,
        author: profiles?.find((p) => p.id === reply.user_id),
      })) as CohortDiscussionReply[];
    },
    enabled: !!discussionId,
  });
}

export function useCreateCohortDiscussionReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discussionId,
      content,
      userId,
    }: {
      discussionId: string;
      content: string;
      userId: string;
    }) => {
      const { data, error } = await supabase
        .from("cohort_discussion_replies")
        .insert({
          discussion_id: discussionId,
          content,
          user_id: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["cohort-discussion-replies", variables.discussionId],
      });
    },
  });
}

export interface MyCohortDiscussion extends CohortDiscussion {
  cohort_name: string;
}

export function useMyCohortDiscussions(userId: string | undefined) {
  return useQuery({
    queryKey: ["my-cohort-discussions", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data: enrollments } = await supabase
        .from("course_enrollments")
        .select("cohort_id")
        .eq("user_id", userId)
        .not("cohort_id", "is", null);

      const cohortIds = [
        ...new Set((enrollments || []).map((e) => e.cohort_id).filter(Boolean) as string[]),
      ];
      if (cohortIds.length === 0) return [];

      const { data: discussions, error } = await supabase
        .from("cohort_discussions")
        .select("*, cohorts!inner(name)")
        .in("cohort_id", cohortIds)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!discussions || discussions.length === 0) return [];

      const userIds = [...new Set(discussions.map((d) => d.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const discussionIds = discussions.map((d) => d.id);
      const { data: replyCounts } = await supabase
        .from("cohort_discussion_replies")
        .select("discussion_id")
        .in("discussion_id", discussionIds);

      const replyCountMap: Record<string, number> = {};
      replyCounts?.forEach((r) => {
        replyCountMap[r.discussion_id] = (replyCountMap[r.discussion_id] || 0) + 1;
      });

      return discussions.map((d) => ({
        ...d,
        author: profiles?.find((p) => p.id === d.user_id),
        reply_count: replyCountMap[d.id] || 0,
        cohort_name: (d.cohorts as { name: string } | null)?.name || "Cohort",
      })) as MyCohortDiscussion[];
    },
    enabled: !!userId,
  });
}
